import { GridApi, RowNode } from "@ag-grid-community/core";

import memoizedDebounce from "@app/util/memoizedDebounce";

import { BASE_GRID_ROW_HEIGHT } from "../../BaseGrid";
import debouncedRowHeightChanged from "../../gridComponents/cellRenderers/utils/debouncedRowHeightChanged";

const rowNodeHeightAdjuster = (node: RowNode, api: GridApi) => {
  // Find all auto-height cells with ag-react-container inside.
  // This method requires to render a <div> in all cell renderers as a container element.
  // It's needed coz AgGrid sometimes renders two react components, one is wrapped in <span>.
  // Probably it does that when re-renders rows to render a new cell before deleting a
  // previous one. But if we will count both, we'll get a double height which is wrong.
  const cellsWithAutoHeightAndReactRenderedCell = document.querySelectorAll(
    `div[row-id="${node.id}"] > div.ag-cell-auto-height div.ag-react-container > div`
  );
  // Now iterate over all dom elements and get max height.
  const maxHeight = Array.from(cellsWithAutoHeightAndReactRenderedCell).reduce(
    (result, element) => (result < element.clientHeight ? element.clientHeight : result),
    BASE_GRID_ROW_HEIGHT
  );

  // console.log("maxHeight=", maxHeight, node.id, cellsWithAutoHeightAndReactRenderedCell.length);

  // Apply row height if it changed
  if (node.rowHeight !== maxHeight) {
    node.setRowHeight(maxHeight);
    debouncedRowHeightChanged(api);
  }
};

const debouncedRowNodeHeightAdjuster = memoizedDebounce(
  rowNodeHeightAdjuster,
  50,
  {
    leading: true,
    maxWait: 500,
    trailing: true,
  },
  {
    cache: {
      // FIXME: pretty dumb cache store implementation which is never reduced in size which can cause memory leaks
      // it's better to limit cache size in some way
      create() {
        const store: { [key: string]: any } = {};
        return {
          get(key): any {
            return store[key];
          },
          has(key) {
            return key in store;
          },
          set(key: string, value: any) {
            store[key] = value;
          },
        };
      },
    },
    serializer: (args: any[]) => (args[0] as RowNode).id,
  }
);

// This hook is adjusting row height when cell is re-rendered.
// Basically we should not do this, but AgGrid fails to adjust row
// height for framework cell renderers. It's a known issue which
// had been fixed in v21.x.x, but it looks like it still exists.
const useRowHeightUpdater = (node: RowNode, api: GridApi) => {
  setTimeout(() => {
    debouncedRowNodeHeightAdjuster(node, api);
  }, 0);
};

export default useRowHeightUpdater;
